import { useCallback, useState } from 'react';
import venueDashboardApiService from '../../../services/venueDashboardApi';
import { normalizeVenueSpacesForDisplay } from '../../../utils/venueSpacesDisplay';

/**
 * Loads operating hours, per-person hours, flat pricing, and per-person dynamic rules for a scope.
 * `spaceId` null = venue-wide rows (space_id IS NULL).
 */
export function useSettingsSpaceScope(venueId, venueSpaces, setters) {
  const [settingsSpaceId, setSettingsSpaceIdState] = useState(null);
  const [scopeLoading, setScopeLoading] = useState(false);

  const multiSpace = normalizeVenueSpacesForDisplay(venueSpaces).length > 1;

  const loadScope = useCallback(
    async (spaceId) => {
      if (!venueId || !setters) return;
      setScopeLoading(true);
      try {
        const [
          hours,
          hoursPerPerson,
          flatPricing,
          dynamicHourly,
          dynamicPerPerson
        ] = await Promise.all([
          venueDashboardApiService.getOperatingHours(venueId, spaceId),
          venueDashboardApiService.getOperatingHoursPerPerson(venueId, spaceId),
          venueDashboardApiService.getFlatPricing(venueId, spaceId),
          venueDashboardApiService.getDynamicPricing(venueId, spaceId),
          venueDashboardApiService.getDynamicPricingPerPerson(venueId, spaceId)
        ]);
        setters.setOperatingHours?.(hours || {});
        setters.setOperatingHoursPerPerson?.(hoursPerPerson || {});
        setters.setFlatPricing?.(Array.isArray(flatPricing) ? flatPricing : []);
        setters.setDynamicPricing?.(Array.isArray(dynamicHourly) ? dynamicHourly : []);
        setters.setDynamicPricingPerPerson?.(Array.isArray(dynamicPerPerson) ? dynamicPerPerson : []);

        if (spaceId) {
          const spaceRecord = (venueSpaces || []).find((s) => String(s.id) === String(spaceId));
          if (spaceRecord?.hourly_price != null) {
            setters.setDefaultPrice?.(Number(spaceRecord.hourly_price));
          }
          if (spaceRecord?.per_person_price != null) {
            setters.setDefaultPerPersonPrice?.(Number(spaceRecord.per_person_price));
          }
        }
      } finally {
        setScopeLoading(false);
      }
    },
    [venueId, venueSpaces, setters]
  );

  const setSettingsSpaceId = useCallback(
    (spaceId) => {
      setSettingsSpaceIdState(spaceId);
      loadScope(spaceId);
    },
    [loadScope]
  );

  return {
    settingsSpaceId,
    setSettingsSpaceId,
    multiSpace,
    scopeLoading
  };
}
